import React, { useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useAuth } from '../../context/AuthContext';
import { dateFilterColumn } from '../../utils/dateFilterColumn';
import {
  useCalibrationSessionsApi, useAllPeriodsMap, STATUS_COLORS, StatusBadge,
} from './config';
import NewSessionDrawer from './components/NewSessionDrawer';
import DataTable from '../../components/ui/DataTable';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import OptionsMenu from '../../components/ui/OptionsMenu';

const Container = styled.div``;

const ScopeBadge = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.dark.little};
`;

// Faz B (spec 2026-08-07 §4, Task 5) — Kalibrasyon Oturumları listesi.
// GET v1/calibration-sessions SAYFALANMAZ (ICalibrationSessionService.List
// düz dizi döner, bkz. config/index.js yorumu — RatingScalesTab.js'in
// serverSide={false} deseniyle BİREBİR).
export default function CalibrationSessionsPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const history = useHistory();
  const { canWrite } = useAuth();
  const canWritePm = canWrite('PM');

  const {
    data, fetching, createSession, runLifecycleAction, deleteDraftSession,
  } = useCalibrationSessionsApi();
  const periodsMap = useAllPeriodsMap();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [lifecycleConfirm, setLifecycleConfirm] = useState({
    isOpen: false, verb: null, rowId: null, title: '', message: '',
  });
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
  const [deleting, setDeleting] = useState(false);

  const goToGrid = useCallback((row) => {
    history.push(`/performance/calibration-sessions/${row.id}`);
  }, [history]);

  const openLifecycleConfirm = useCallback((verb, row) => {
    const messageKey = verb === 'activate'
      ? 'performance.calibrationSession.activateConfirm'
      : 'performance.calibrationSession.cancelConfirm';
    setLifecycleConfirm({
      isOpen: true,
      verb,
      rowId: row.id,
      title: t(`performance.calibrationSession.${verb}`),
      message: t(messageKey),
    });
  }, [t]);

  const handleLifecycleConfirm = useCallback(async () => {
    setLifecycleLoading(true);
    await runLifecycleAction(lifecycleConfirm.rowId, lifecycleConfirm.verb);
    setLifecycleConfirm({
      isOpen: false, verb: null, rowId: null, title: '', message: '',
    });
    setLifecycleLoading(false);
  }, [runLifecycleAction, lifecycleConfirm]);

  const handleDeleteConfirm = useCallback(async () => {
    setDeleting(true);
    await deleteDraftSession(deleteConfirm.id);
    setDeleteConfirm({ isOpen: false, id: null, name: '' });
    setDeleting(false);
  }, [deleteDraftSession, deleteConfirm.id]);

  const buildActionOptions = useCallback((row) => {
    const actions = [{ type: 'open', label: t('performance.calibrationSession.openGrid'), onClick: goToGrid }];
    if (!canWritePm) return actions;

    if (row.status === 'draft') {
      actions.push({ type: 'submit_for_approval', label: t('performance.calibrationSession.activate'), onClick: (r) => openLifecycleConfirm('activate', r) });
      actions.push({
        type: 'delete',
        label: t('common.delete'),
        onClick: (r) => setDeleteConfirm({ isOpen: true, id: r.id, name: r.name }),
      });
    }
    if (row.status === 'active') {
      actions.push({ type: 'cancel', label: t('performance.calibrationSession.cancel'), onClick: (r) => openLifecycleConfirm('cancel', r) });
    }
    return actions;
  }, [t, canWritePm, goToGrid, openLifecycleConfirm]);

  const columns = useMemo(() => [
    { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
    {
      name: 'name',
      label: t('performance.calibrationSession.name'),
      options: {
        filter: true, sort: true, filterType: 'textField', filterOptions: { defaultOp: 'like' },
      },
    },
    {
      name: 'periodId',
      label: t('performance.calibrationSession.period'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v) => periodsMap[v] || '-',
      },
    },
    {
      name: 'status',
      label: t('common.status'),
      options: {
        filter: true, sort: false,
        customBodyRender: (v) => {
          const cfg = STATUS_COLORS[v] || STATUS_COLORS.draft;
          return (
            <StatusBadge color={cfg.color} bg={cfg.bg}>
              {t(`performance.calibrationSession.status.${v}`, { defaultValue: v })}
            </StatusBadge>
          );
        },
      },
    },
    {
      name: 'scopeType',
      label: t('performance.calibrationSession.form.scopeType'),
      options: {
        filter: false, sort: false,
        customBodyRender: (v, tableMeta) => {
          const row = data[tableMeta.rowIndex];
          if (!row) return null;
          const label = t(`performance.calibrationSession.scope.${v}`, { defaultValue: v });
          return (
            <ScopeBadge>
              {label}{v !== 'company' ? ` (${row.scopeTargetIds?.length || 0})` : ''}
            </ScopeBadge>
          );
        },
      },
    },
    {
      name: 'members',
      label: t('performance.calibrationSession.form.moderator'),
      options: {
        filter: false, sort: false,
        customBodyRender: (members) => {
          const moderator = (members || []).find((m) => m.role === 'moderator');
          return moderator ? moderator.userName : '-';
        },
      },
    },
    // Tablo istemci tarafında (serverSide={false}) — süzgeç tarayıcıda uygulanmalı.
    dateFilterColumn('createDate', t('performance.common.createDate'), lang, { clientSide: true }),
  ], [t, lang, periodsMap, data]);

  return (
    <Container>
      {/* UX standardı (2026-08-10): oluşturma modal değil GenericFormDrawer ile. */}
      <NewSessionDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onCreate={createSession}
      />

      <ConfirmDialog
        isOpen={lifecycleConfirm.isOpen}
        title={lifecycleConfirm.title}
        message={lifecycleConfirm.message}
        onConfirm={handleLifecycleConfirm}
        onCancel={() => setLifecycleConfirm({
          isOpen: false, verb: null, rowId: null, title: '', message: '',
        })}
        loading={lifecycleLoading}
        type={lifecycleConfirm.verb === 'cancel' ? 'danger' : 'info'}
      />

      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        title={t('performance.calibrationSession.deleteConfirmTitle')}
        message={t('performance.calibrationSession.deleteConfirmMessage', { name: deleteConfirm.name })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
        loading={deleting}
        type="danger"
      />

      {useMemo(() => (
        <DataTable
          title={t('performance.calibrationSession.title')}
          fetching={fetching}
          columns={[
            ...columns,
            {
              name: 'actions',
              label: ' ',
              options: {
                filter: false, sort: false, download: false, print: false,
                customBodyRender: (_, tableMeta) => {
                  const row = data[tableMeta.rowIndex];
                  if (!row) return null;
                  return <OptionsMenu actions={buildActionOptions(row)} row={row} />;
                },
              },
            },
          ]}
          data={data}
          serverSide={false}
          canCreateNewRecord={canWritePm}
          newButtonLabel={t('performance.calibrationSession.new')}
          onNewButtonClick={() => setDrawerOpen(true)}
          onRowClick={goToGrid}
        />
      ), [t, fetching, columns, data, canWritePm, buildActionOptions, goToGrid])}
    </Container>
  );
}
